import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { Banknote, Check } from 'lucide-react';
import '../nativeinventory/NativeInventoryAdjustmentNew.css';
import '../nativeitems/NativeItems.css';

// New Customer Payment (Native) — records an AR payment natively first
// (document + JE: DR Undeposited Funds / CR AR), then best-effort in NetSuite
// with the card-safety fields (the server refuses any payload that could
// charge a stored card). Modeled on NativeEntityNew.
//
// Flow: pick a customer -> their open invoices load from the mirror -> check
// the ones this payment covers (amounts default to the remaining balance,
// editable for partial payments) -> post -> confirmation with leg statuses.

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export default function NativePaymentNew() {
  const [customer, setCustomer] = useState(null); // { nsCustomerId, name, email }
  const [custQuery, setCustQuery] = useState('');
  const [custResults, setCustResults] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesNote, setInvoicesNote] = useState(null);
  const [selected, setSelected] = useState({}); // invoice id -> amount string
  const [amount, setAmount] = useState('');
  const [amountTouched, setAmountTouched] = useState(false);
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [paymentMethod, setPaymentMethod] = useState('check');
  const [memo, setMemo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  // One sourceKey per form fill: double-clicking Post can never double-post.
  const sourceKeyRef = useRef(`UI-PAY-${Date.now()}-${Math.floor(Math.random() * 1e6)}`);

  useEffect(() => {
    if (custQuery.trim().length < 2 || customer) { setCustResults([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get('/api/native-orders/customers', { params: { q: custQuery.trim() } });
        setCustResults(data.customers || []);
      } catch { setCustResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [custQuery, customer]);

  useEffect(() => {
    if (!customer) { setInvoices([]); setSelected({}); return; }
    setInvoicesLoading(true);
    setInvoicesNote(null);
    axios.get('/api/native-payments/open-invoices', { params: { nsCustomerId: customer.nsCustomerId } })
      .then(({ data }) => { setInvoices(data.invoices || []); setInvoicesNote(data.note || null); })
      .catch(() => setInvoicesNote('Could not load open invoices — you can still record an unapplied payment.'))
      .finally(() => setInvoicesLoading(false));
  }, [customer]);

  const appliedTotal = useMemo(
    () => Object.values(selected).reduce((s, v) => s + (Number(v) || 0), 0),
    [selected]
  );

  // Until the user edits the amount by hand, it tracks the applied total.
  useEffect(() => {
    if (!amountTouched && appliedTotal > 0) setAmount(String(Math.round(appliedTotal * 100) / 100));
  }, [appliedTotal, amountTouched]);

  const toggleInvoice = (inv) => {
    setSelected((sel) => {
      const next = { ...sel };
      if (next[inv.id] != null) delete next[inv.id];
      else next[inv.id] = String(inv.amount_remaining ?? inv.total_amount ?? '');
      return next;
    });
  };

  const amountNum = Number(amount);
  const canPost = customer && Number.isFinite(amountNum) && amountNum > 0
    && appliedTotal <= amountNum + 0.005
    && Object.values(selected).every((v) => Number(v) > 0);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const applications = invoices
        .filter((inv) => selected[inv.id] != null)
        .map((inv) => (inv.ns_internal_id != null
          ? { nsInvoiceId: String(inv.ns_internal_id), amount: Number(selected[inv.id]) }
          : { nativeInvoiceId: inv.id, amount: Number(selected[inv.id]) }));
      const { data } = await axios.post('/api/native-payments', {
        sourceKey: sourceKeyRef.current,
        customer: { nsId: customer.nsCustomerId },
        amount: amountNum,
        paymentDate,
        paymentMethod: paymentMethod || undefined,
        memo: memo.trim() || undefined,
        applications,
      });
      setConfirmation(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    const p = confirmation.payment || {};
    return (
      <div className="nia-page">
        <div className="nia-card nia-confirm">
          <div className="nia-confirm-icon"><Check size={28} /></div>
          <h2>{p.payment_number} posted — {fmt(p.amount)}</h2>
          <div className="nia-confirm-grid">
            <span>Customer</span><strong>{p.customer_name || p.ns_customer_id}</strong>
            <span>Applied</span><strong>{fmt(p.applied_total)} across {p.application_count} invoice{Number(p.application_count) === 1 ? '' : 's'}</strong>
            <span>GL</span>
            <strong>{p.gl_status === 'posted' ? 'posted (DR Undeposited Funds / CR AR)' : `${p.gl_status || 'unknown'}${p.gl_error ? ` — ${p.gl_error}` : ''}`}</strong>
            <span>NetSuite</span>
            <strong>
              {p.ns_status === 'linked' || p.ns_status === 'mirror_superseded'
                ? `created (${p.ns_transaction_id})`
                : p.ns_status === 'skipped'
                  ? 'skipped — native record is the book'
                  : `pending — ${p.ns_error || 'retry when NS is reachable'}`}
            </strong>
          </div>
          <div className="nia-actions">
            <button className="nia-btn nia-btn-primary" onClick={() => window.location.reload()}>New payment</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="nia-page">
      <div className="nia-header">
        <h1><Banknote size={20} /> New Customer Payment (Native)</h1>
      </div>

      <div className="nia-card">
        <h3>1. Customer</h3>
        {!customer ? (
          <>
            <input className="nia-input nia-input-search" placeholder="Search customers…" value={custQuery} onChange={(e) => setCustQuery(e.target.value)} />
            {custResults.length > 0 && (
              <div className="nia-results">
                {custResults.map((c) => (
                  <button key={c.nsCustomerId} className="nia-result" onClick={() => { setCustomer(c); setCustQuery(''); setCustResults([]); }}>
                    <strong>{c.name}</strong>{c.email && <em>{c.email}</em>}
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="nia-chip">
            {customer.name}
            <button className="nia-icon-btn" onClick={() => { setCustomer(null); setSelected({}); setAmount(''); setAmountTouched(false); }}>×</button>
          </div>
        )}
      </div>

      {customer && (
        <div className="nia-card">
          <h3>2. Apply to open invoices (optional)</h3>
          <p className="nia-hint">Leave everything unchecked to record an unapplied payment. Amounts default to the balance remaining — edit for a partial payment.</p>
          {invoicesLoading ? (
            <p className="nia-hint">Loading open invoices…</p>
          ) : invoices.length === 0 ? (
            <p className="nia-hint">{invoicesNote || 'No open invoices in the mirror for this customer.'}</p>
          ) : (
            <table className="nit-table" style={{ width: '100%' }}>
              <thead>
                <tr><th></th><th>Invoice</th><th>Date</th><th>Due</th><th style={{ textAlign: 'right' }}>Remaining</th><th style={{ textAlign: 'right' }}>Apply</th></tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id}>
                    <td><input type="checkbox" checked={selected[inv.id] != null} onChange={() => toggleInvoice(inv)} /></td>
                    <td>{inv.invoice_number || inv.ns_internal_id}{inv.ns_internal_id == null && <em> (native)</em>}</td>
                    <td>{inv.invoice_date || ''}</td>
                    <td>{inv.due_date || ''}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(inv.amount_remaining)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {selected[inv.id] != null && (
                        <input
                          className="nia-input"
                          style={{ width: 110, textAlign: 'right' }}
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={selected[inv.id]}
                          onChange={(e) => setSelected((sel) => ({ ...sel, [inv.id]: e.target.value }))}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {customer && (
        <div className="nia-card">
          <h3>3. Payment</h3>
          <div className="nit-form" style={{ maxWidth: 480 }}>
            <label><span>Amount received</span>
              <input type="number" min="0.01" step="0.01" value={amount} onChange={(e) => { setAmount(e.target.value); setAmountTouched(true); }} />
            </label>
            <label><span>Date</span>
              <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
            </label>
            <label><span>Method</span>
              <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                <option value="check">Check</option>
                <option value="ach">ACH</option>
                <option value="wire">Wire</option>
                <option value="cash">Cash</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label><span>Memo (optional)</span>
              <input value={memo} onChange={(e) => setMemo(e.target.value)} maxLength={500} />
            </label>
          </div>
          {appliedTotal > 0 && (
            <p className="nia-hint" style={{ marginTop: 8 }}>
              Applying {fmt(appliedTotal)} of {Number.isFinite(amountNum) ? fmt(amountNum) : '—'}
              {Number.isFinite(amountNum) && amountNum - appliedTotal > 0.005 && ` — ${fmt(amountNum - appliedTotal)} will stay unapplied`}
              {Number.isFinite(amountNum) && appliedTotal > amountNum + 0.005 && ' — applications exceed the amount received'}
            </p>
          )}
          {error && <div className="nia-error" style={{ marginTop: 10 }}>{error}</div>}
          <div className="nia-actions" style={{ marginTop: 12 }}>
            <button className="nia-btn nia-btn-primary" onClick={submit} disabled={submitting || !canPost}>
              {submitting ? 'Posting…' : 'Post payment'}
            </button>
          </div>
          <p className="nia-hint" style={{ marginTop: 8 }}>
            Posts natively first (DR Undeposited Funds / CR AR), then creates the NetSuite copy — never charging a stored card.
          </p>
        </div>
      )}
    </div>
  );
}
